/**
 * What a feedback report is allowed to know about the page.
 *
 * The rule this file exists to enforce: collect enough to reproduce the
 * problem, and nothing else. No page HTML, no form values, no query strings.
 * Everything here is a pure function of the DOM so it can be unit-tested
 * without a running app.
 */

/** Elements we consider a sensible pick target when walking by keyboard. */
const CANDIDATE_SELECTOR = [
  '[data-test]',
  '[data-panel-id]',
  '.highcharts-container',
  'table',
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  '[role="button"]',
  '[role="tab"]',
  '[role="dialog"]',
  'section',
  'article',
].join(',');

/** Attributes whose value may safely be read as an identifier. */
const SUBJECT_ATTRIBUTES = {
  dashboard_id: 'data-dashboard-id',
  panel_id: 'data-panel-id',
  visualization_id: 'data-visualization-id',
  dataset_id: 'data-dataset-id',
};

/**
 * Strip query strings from a location, in the path AND in the hash route.
 *
 * The app uses hash routing, so a share token lives at `/#/dash?token=…` —
 * cutting only the first `?` would still ship it. Mirrors
 * `sanitize_location()` in the backend service, which re-applies this on the
 * trust boundary; this copy keeps the token out of the browser's own draft.
 */
export function sanitizeLocation(value) {
  if (!value) return null;
  const [beforeHash, ...rest] = String(value).split('#');
  const path = beforeHash.split('?')[0];
  const fragment = rest.join('#');
  if (!fragment) return path || '/';
  return `${path}#${fragment.split('?')[0]}`;
}

/**
 * Which app this build is. Both apps are hash-routed on their own origin, so
 * the page path alone does not say whether a customer or an operator filed
 * the report.
 */
export function appSurface() {
  return process.env.VUE_APP_BUILD_TARGET === 'admin' ? 'admin' : 'portal';
}

export function collectPageContext(route = null) {
  if (typeof window === 'undefined') return {};
  return {
    path: sanitizeLocation(`${window.location.pathname}${window.location.hash}`),
    route_name: route?.name ? String(route.name) : null,
    title: document.title ? document.title.slice(0, 300) : null,
    // Our own host, not user data. The reviewer sits in a different app on a
    // different origin, so a path alone cannot be turned back into a link —
    // and a report from acceptance must not open production.
    origin: window.location.origin || null,
  };
}

export function collectClientContext() {
  if (typeof window === 'undefined') return {};
  return {
    user_agent: navigator.userAgent?.slice(0, 400) || null,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio || 1,
    locale: navigator.language || null,
    theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  };
}

/** Short, human-readable label for an element — never its markup. */
function elementLabel(element) {
  const aria = element.getAttribute?.('aria-label');
  if (aria) return aria.trim().slice(0, 200);
  const labelledBy = element.getAttribute?.('aria-labelledby');
  if (labelledBy) {
    const target = document.getElementById(labelledBy);
    if (target?.textContent?.trim()) return target.textContent.trim().slice(0, 200);
  }
  // An input's own value is deliberately NOT read — only its label.
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)) {
    const label = element.labels?.[0]?.textContent?.trim();
    if (label) return label.slice(0, 200);
    const placeholder = element.getAttribute('placeholder');
    return placeholder ? placeholder.trim().slice(0, 200) : null;
  }
  // A container's textContent runs every descendant together and reads as
  // gibberish in a report. Its heading is what a human would call the thing.
  const heading = element.querySelector?.('h1, h2, h3, h4, h5, h6, legend, caption');
  const headingText = heading?.textContent?.replace(/\s+/g, ' ').trim();
  if (headingText) return headingText.slice(0, 200);

  const text = element.textContent?.replace(/\s+/g, ' ').trim();
  return text ? text.slice(0, 200) : null;
}

/** A short structural selector — enough to find the element again. */
function elementSelector(element) {
  const parts = [];
  let node = element;
  for (let depth = 0; node && node.nodeType === 1 && depth < 4; depth += 1) {
    const testId = node.getAttribute('data-test');
    if (testId) {
      parts.unshift(`[data-test="${testId}"]`);
      break;
    }
    const tag = node.tagName.toLowerCase();
    // Class names are layout, not content — safe, and they make the selector
    // recognisable. Only the first two, so a Tailwind soup stays readable.
    const classes = Array.from(node.classList || [])
      .filter((name) => !name.startsWith('router-link'))
      .slice(0, 2)
      .map((name) => `.${name}`)
      .join('');
    parts.unshift(`${tag}${classes}`);
    node = node.parentElement;
  }
  return parts.join(' > ').slice(0, 300);
}

/**
 * The element the current text selection sits in.
 *
 * Selecting a sentence says what is wrong *and* where it is, so the reporter
 * should not then have to point at the same thing again with the picker. The
 * common ancestor of the range is the honest answer: for a selection inside
 * one paragraph that is the paragraph, and for one spanning a table it is the
 * table. Walks up out of text nodes, and refuses our own UI so selecting text
 * inside the panel never reports the panel.
 */
export function elementFromSelection() {
  if (typeof window === 'undefined') return null;
  const selection = window.getSelection?.();
  if (!selection || selection.isCollapsed || !selection.rangeCount) return null;
  let node = selection.getRangeAt(0).commonAncestorContainer;
  while (node && node.nodeType !== 1) node = node.parentNode;
  if (!node || node.closest?.('[data-feedback-ui]')) return null;
  return node;
}

export function describeElement(element) {
  if (!element || element.nodeType !== 1) return null;
  return {
    label: elementLabel(element),
    test_id: element.getAttribute('data-test') || null,
    role: element.getAttribute('role') || element.tagName.toLowerCase(),
    selector: elementSelector(element),
  };
}

/**
 * Dashboard / chart identifiers around an element, when it sits inside one.
 * Read from data attributes the visualization components already render.
 */
export function describeSubject(element) {
  if (!element || element.nodeType !== 1) return null;
  const subject = {};
  Object.entries(SUBJECT_ATTRIBUTES).forEach(([key, attribute]) => {
    const host = element.closest(`[${attribute}]`);
    const value = host?.getAttribute(attribute);
    if (value) subject[key] = value.slice(0, 200);
  });
  return Object.keys(subject).length ? subject : null;
}

/**
 * Elements a keyboard user can step through while picking. Hover is one way
 * to aim; this list is the other, so the picker never requires a pointer.
 */
export function collectPickCandidates(root = document.body) {
  const seen = new Set();
  return Array.from(root.querySelectorAll(CANDIDATE_SELECTOR)).filter((element) => {
    if (seen.has(element) || element.closest('[data-feedback-ui]')) return false;
    const rect = element.getBoundingClientRect();
    if (rect.width < 16 || rect.height < 16) return false;
    seen.add(element);
    return true;
  });
}
